import clsx from 'clsx'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import { Controller, FieldErrors, UseFormGetValues } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import s from './styles.module.scss'

import {
  ModalSelectProposalSelectOptionsCorporateEveningEvent,
  ModalSelectProposalSelectOptionsCorporateEveningEventProps,
} from './components/corporate-evening-event/ModalSelectProposalSelectOptionsCorporateEveningEvent'
import { DatesPickerProps } from './dates-adults-picker/dates-picker/DatesPicker'
import { DatesAdultsPicker } from './dates-adults-picker/DatesAdultsPicker'
import { isIndividualHouse } from './utils'
import ModalSelectProposalArrivalButtonGroup from '../components/modal-select-proposal-arrival-button-group/ModalSelectProposalArrivalButtonGroup'
import ModalSelectProposalDepartureButtonGroup from '../components/modal-select-proposal-departure-button-group/ModalSelectProposalDepartureButtonGroup'
import { MODAL_SELECT_PROPOSAL_MODE } from '../modal-select-proposal.enum'
import { ISelectProposalForm } from '../modal-select-proposal.types'
import {
  DefaultContent,
  ExecutiveContent,
} from '../../house-card-content/HouseCardContent'
import {
  ArrivalEnum,
  DepartureEnum,
  HouseBookingTypeEnum,
  HousePriceTypeEnum,
  HousesInformationsFieldsFragment,
  TravelTypeEnum,
} from '../../../../lib/graphql'
import { Mixpanel } from '../../../../utils/Mixpanel'
import { IconSvg, ToggleButton } from '../../../atoms'
import { toggleColorEnum } from '../../../atoms/fields/toggle-button/toggleColorEnum'
import { useTravelTypeContext, useUserTypeContext } from '../../../contexts'
import { INPUT_TYPES, InputTypeText } from '../../../forms'
import {
  DEFAULT_HOUR_PICKER_OPTIONS,
  DEFAULT_MIDNIGHT_HOUR_PICKER_OPTION,
} from '../../../forms/hour-picker/ControlledHourPicker'

interface IModalSelectProposalSelectOptions
  extends Omit<DatesPickerProps, 'dates'> {
  mode: MODAL_SELECT_PROPOSAL_MODE
  errors: FieldErrors<ISelectProposalForm>
  house: HousesInformationsFieldsFragment
  getValues: UseFormGetValues<ISelectProposalForm>
}

export const ModalSelectProposalSelectOptions = ({
  control,
  house,
  mode,
  errors,
  getValues,
  ...remainingPickerProps
}: IModalSelectProposalSelectOptions) => {
  const intl = useIntl()
  const dates = getValues('dates')
  const arrival = getValues('arrival')
  const debounceDelayMicroSeconds = 500
  const numberOfIndividualBedrooms = getValues('individualBedroomAdults')
  const numberOfSharedBedrooms = getValues('sharedBedroomAdults')
  const { travelType } = useTravelTypeContext()
  const { isAdmin } = useUserTypeContext()

  const [showTooltip, setShowTooltip] = useState(false)

  const handleMouseOver = () => {
    if (house?.priceType !== HousePriceTypeEnum.GLOBAL) {
      setShowTooltip(true)
    }
  }

  const handleMouseOut = () => {
    setShowTooltip(false)
  }

  return (
    <div
      className={clsx(s.container, {
        [s.inHouseCardContainer]:
          mode === MODAL_SELECT_PROPOSAL_MODE.IN_HOUSE_CARD,
      })}
    >
      {mode === MODAL_SELECT_PROPOSAL_MODE.DEFAULT && (
        <div className={s.description}>
          <p>
            <FormattedMessage
              id='ui.ModalSelectProposalSelectOptions.selectOptions'
              defaultMessage='Sélectionnez vos options ci-dessous afin de définir les formules adaptées'
            />
          </p>
        </div>
      )}
      <div className={s.optionsContainer}>
        {mode === MODAL_SELECT_PROPOSAL_MODE.DEFAULT && (
          <div
            className={clsx(s.houseContainer, {
              [s.isExecutive]: house?.isExecutive,
            })}
          >
            {house?.isExecutive ? (
              <ExecutiveContent house={house} />
            ) : (
              <DefaultContent house={house} />
            )}
          </div>
        )}
        <div className={s.formContainer}>
          <DatesAdultsPicker
            control={control}
            house={house}
            dates={dates}
            errors={errors}
            {...remainingPickerProps}
          />

          {travelType === TravelTypeEnum.NIGHTLY_TRIP ||
          travelType === TravelTypeEnum.DAILY_TRIP ? (
            <>
              <div className={s.scheduleItem} data-gtm='toggle-arrival'>
                <h5>
                  <FormattedMessage
                    id='ui.ModalSelectProposalSelectOptions.arrival'
                    defaultMessage='Arrivée'
                  />
                </h5>
                <ModalSelectProposalArrivalButtonGroup
                  control={control}
                  orderedArrivals={[
                    ArrivalEnum.MORNING,
                    ArrivalEnum.BEFORE_LUNCH,
                    ...(travelType === TravelTypeEnum.NIGHTLY_TRIP
                      ? [ArrivalEnum.EVENING]
                      : []),
                  ]}
                />
              </div>
              <div className={s.scheduleItem} data-gtm='toggle-departure'>
                <h5>
                  <FormattedMessage
                    id='ui.ModalSelectProposalSelectOptions.departure'
                    defaultMessage='Départ'
                  />
                </h5>
                {travelType === TravelTypeEnum.NIGHTLY_TRIP && (
                  <ModalSelectProposalDepartureButtonGroup
                    control={control}
                    orderedDepartures={[
                      {
                        value: DepartureEnum.MORNING,
                        disabled:
                          arrival === ArrivalEnum.EVENING &&
                          dates.startDate &&
                          dates.endDate &&
                          dayjs(dates.endDate).diff(dates.startDate, 'day') < 2,
                        tooltipsProps: {
                          content: intl.formatMessage({
                            id: 'ui.DepartureToggleButton.canOnlyBeSelectedIfArrivalNotEveningOrIfThereMoreThanOneNight',
                            defaultMessage: `Ce départ est disponible si l'arrivée n'est pas le soir ou s'il y a plus d'une nuit`,
                          }),
                          position: 'bottom-right',
                        },
                      },
                      {
                        value: DepartureEnum.AFTER_LUNCH,
                      },
                      { value: DepartureEnum.EVENING },
                    ]}
                  />
                )}

                {travelType === TravelTypeEnum.DAILY_TRIP && (
                  <ModalSelectProposalDepartureButtonGroup
                    control={control}
                    orderedDepartures={[
                      {
                        value: DepartureEnum.AFTER_LUNCH,
                        disabled:
                          dates.startDate &&
                          dates.endDate &&
                          arrival === ArrivalEnum.BEFORE_LUNCH &&
                          dayjs(dates.startDate).isSame(dates.endDate, 'day'),
                        tooltipsProps: {
                          content: intl.formatMessage({
                            id: 'ui.DepartureToggleButton.canOnlyBeSelectedIfArrivalNotBeforeLunchOrDatesAreDifferentDays',
                            defaultMessage: `Ce départ est disponible si l'arrivée n'est pas avant le déjeuner ou que les dates d'arrivée et de départ sont différentes`,
                          }),
                          position: 'bottom',
                        },
                      },
                      { value: DepartureEnum.EVENING },
                    ]}
                  />
                )}
              </div>
            </>
          ) : null}

          {travelType === TravelTypeEnum.NIGHTLY_TRIP ? (
            <>
              {isIndividualHouse(house) && (
                <div className={s.participantsContainer}>
                  <div className={s.participantsItem}>
                    <h5>
                      <FormattedMessage
                        id='ui.ModalSelectProposalSelectOptions.singleBedroomParticipants'
                        defaultMessage='Participants en chambres <b>single</b>'
                        values={{
                          b: (chunks) => <b>{chunks}</b>,
                        }}
                      />
                    </h5>
                    <div
                      data-gtm='modal-select-proposal-single'
                      onClick={() => Mixpanel.track('input-single')}
                    >
                      <InputTypeText
                        control={control}
                        name='individualBedroomAdults'
                        classNameOnFocus={s.input}
                        rules={{
                          validate: (value, formValues) => {
                            return formValues.individualBedroomAdults ||
                              formValues.sharedBedroomAdults
                              ? true
                              : intl.formatMessage({
                                  id: 'ui.ModalSelectProposalSelectOptions.incompleteField',
                                  defaultMessage: 'Champ incomplet',
                                })
                          },
                        }}
                        inputType={INPUT_TYPES.INT}
                        max={house.numberOfBedrooms}
                        placeholder={intl.formatMessage({
                          id: 'ui.ModalSelectProposalSelectOptions.enterHere',
                          defaultMessage: 'Écrire ici',
                        })}
                        debounceDelayMicroSeconds={debounceDelayMicroSeconds}
                        value={numberOfIndividualBedrooms?.toString()}
                      />
                    </div>
                  </div>
                  <div className={s.participantsItem}>
                    <h5>
                      <FormattedMessage
                        id='ui.ModalSelectProposalSelectOptions.twinBedroomParticipants'
                        defaultMessage='Participants en chambres <b>twin</b>'
                        values={{
                          b: (chunks) => <b>{chunks}</b>,
                        }}
                      />
                    </h5>
                    <div
                      data-gtm='modal-select-proposal-twin'
                      onClick={() => Mixpanel.track('input-twin')}
                    >
                      <InputTypeText
                        control={control}
                        name='sharedBedroomAdults'
                        classNameOnFocus={s.input}
                        rules={{
                          validate: (value, formValues) => {
                            if (
                              !isAdmin &&
                              value &&
                              parseInt(value) % 2 !== 0
                            ) {
                              return intl.formatMessage({
                                id: 'ui.ModalSelectProposalSelectOptions.selectOddNumber',
                                defaultMessage:
                                  'Veuillez saisir un nombre pair',
                              })
                            }

                            return formValues.individualBedroomAdults ||
                              formValues.sharedBedroomAdults
                              ? true
                              : intl.formatMessage({
                                  id: 'ui.ModalSelectProposalSelectOptions.incompleteField',
                                  defaultMessage: 'Champ incomplet',
                                })
                          },
                        }}
                        disable={
                          house.numberOfTwins <= 0 &&
                          house.residentialBookingType !==
                            HouseBookingTypeEnum.QUOTATION
                        }
                        inputType={INPUT_TYPES.INT}
                        max={house.numberOfBeds}
                        placeholder={intl.formatMessage({
                          id: 'ui.ModalSelectProposalSelectOptions.enterHere',
                          defaultMessage: 'Écrire ici',
                        })}
                        debounceDelayMicroSeconds={debounceDelayMicroSeconds}
                        value={numberOfSharedBedrooms?.toString()}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div
                className={s.restaurationContainer}
                data-gtm='toggle-restauration'
                onClick={() => {
                  if (house?.priceType === HousePriceTypeEnum.INDIVIDUAL) {
                    Mixpanel.track('toggle-restauration', { statut: 'locked' })
                  } else {
                    Mixpanel.track('toggle-restauration', {
                      statut: 'unlocked',
                    })
                  }
                }}
              >
                <h5>
                  <FormattedMessage
                    id='ui.ModalSelectProposalSelectOptions.cateringFormat'
                    defaultMessage='Format restauration'
                  />
                </h5>
                <Controller
                  control={control}
                  name='restauration'
                  render={({ field }) => {
                    return (
                      <div className={s.completePension}>
                        <ToggleButton
                          nameLeft={
                            <div
                              className={s.nameLeft}
                              onMouseOver={handleMouseOver}
                              onMouseOut={handleMouseOut}
                            >
                              <FormattedMessage
                                id='ui.ModalSelectProposalSelectOptions.fullBoard'
                                defaultMessage='Pension complète'
                              />
                              {house?.priceType ===
                                HousePriceTypeEnum.INDIVIDUAL && (
                                <IconSvg.Lock
                                  color='black'
                                  height='14px'
                                  width='10px'
                                />
                              )}
                            </div>
                          }
                          nameRight={intl.formatMessage({
                            id: 'ui.ModalSelectProposalSelectOptions.dryHire',
                            defaultMessage: 'Location sèche',
                          })}
                          value={field.value}
                          setValue={(value) => {
                            if (
                              house?.priceType === HousePriceTypeEnum.GLOBAL
                            ) {
                              field.onChange(value)
                            }
                          }}
                          disabled={isIndividualHouse(house)}
                          className={s.restaurationSwitch}
                          toggleColor={toggleColorEnum.BLACK}
                          dataTestId='restauration-toggle'
                        />
                        {showTooltip && (
                          <div className={s.toolTip}>
                            <FormattedMessage
                              id='ui.ModalSelectProposalSelectOptions.fullBoardToolTip'
                              defaultMessage='La pension complète est obligatoire dans les lieux opérés (hôtels, lieux de séminaires...)'
                            />
                          </div>
                        )}
                      </div>
                    )
                  }}
                />
              </div>
            </>
          ) : travelType === TravelTypeEnum.CORPORATE_EVENING_EVENT ? (
            <ModalSelectProposalSelectOptionsCorporateEveningEvent
              hoursOptions={computeCorporateEveningEventHoursOptions({
                options: house.options,
              })}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}

const computeCorporateEveningEventHoursOptions = ({
  options: { corporateEvening },
}: Pick<
  HousesInformationsFieldsFragment,
  'options'
>): ModalSelectProposalSelectOptionsCorporateEveningEventProps['hoursOptions'] => {
  if (corporateEvening.options?.hours) {
    const { overStayMaxAt, startAt } = corporateEvening.options.hours

    const startIndex = DEFAULT_HOUR_PICKER_OPTIONS.findIndex(
      ({ value }) => value === startAt,
    )
    const endIndex = DEFAULT_HOUR_PICKER_OPTIONS.findIndex(
      ({ value }) => value === overStayMaxAt,
    )

    /**
     * --:--, startAt to 00:00
     */
    const startAtOptions = [
      ...DEFAULT_HOUR_PICKER_OPTIONS.slice(startIndex),
      DEFAULT_MIDNIGHT_HOUR_PICKER_OPTION,
    ]
    /**
     * --:--, (startAt + 1) to overStayLimitHour
     */
    const overStayMaxAtOptions = [
      ...startAtOptions.slice(1),
      ...DEFAULT_HOUR_PICKER_OPTIONS.slice(2, endIndex + 1),
    ]

    return { startAtOptions, overStayMaxAtOptions }
  }

  return null
}
